import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, Globe2, FileText, Users, CalendarDays } from "lucide-react";
import { submitForm } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { FadeIn, Kicker, SectionShell, MaskedLine } from "../components/Reveal";
import SEO from "../components/SEO";

const HERO_IMG = "https://images.pexels.com/photos/31930001/pexels-photo-31930001.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const WHY_ICONS = [Globe2, Users, CalendarDays, FileText];

const inputCls = "w-full border border-border bg-background px-4 py-3 text-sm transition-colors placeholder:text-muted-foreground/60";

function SupportForm() {
  const { d } = useI18n();
  const s = d.wrdPage.support;
  const [form, setForm] = useState({ name: "", email: "", country: "", organization: "", position: "" });
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await submitForm({ type: "supporter", ...form, consent_publish: consent });
      setDone(res.data);
      toast.success(s.toastOk);
    } catch {
      toast.error(s.toastErr);
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="corner-ticks border border-border bg-background p-10" data-testid="wrd-support-success">
        <CheckCircle2 size={36} className="text-foliage dark:text-stalk" />
        <h3 className="mt-6 font-serif text-2xl">{s.thanks}, {done.name.split(" ")[0]}.</h3>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          {s.successBase}
          {done.consent_publish ? s.successConsent : s.successNoConsent}
        </p>
        <p className="mt-6 inline-block border border-border px-4 py-2 font-mono text-xs tracking-[0.2em] text-crimson dark:text-stalk" data-testid="wrd-support-reference">REF · {done.reference}</p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="corner-ticks border border-border bg-background p-8 sm:p-10" data-testid="wrd-support-form">
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{s.formTitle}</p>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <input required value={form.name} onChange={set("name")} placeholder={s.ph.name} className={inputCls} data-testid="wrd-support-name-input" />
        <input required type="email" value={form.email} onChange={set("email")} placeholder={s.ph.email} className={inputCls} data-testid="wrd-support-email-input" />
        <input value={form.country} onChange={set("country")} placeholder={s.ph.country} className={inputCls} data-testid="wrd-support-country-input" />
        <input value={form.organization} onChange={set("organization")} placeholder={s.ph.org} className={inputCls} data-testid="wrd-support-organization-input" />
        <input value={form.position} onChange={set("position")} placeholder={s.ph.position} className={`${inputCls} sm:col-span-2`} data-testid="wrd-support-position-input" />
      </div>
      <label className="mt-5 flex cursor-pointer items-start gap-3 text-sm text-foreground/80">
        <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-1 h-4 w-4 accent-crimson" data-testid="wrd-support-consent-checkbox" />
        {s.consent}
      </label>
      <button
        type="submit"
        disabled={busy}
        data-testid="wrd-support-submit-button"
        className="group mt-6 flex w-full items-center justify-center gap-3 border border-crimson bg-crimson px-6 py-4 font-mono text-xs uppercase tracking-[0.25em] text-parchment transition-colors hover:bg-transparent hover:text-crimson disabled:opacity-60"
      >
        {busy ? s.submitting : s.submit}
        <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
      </button>
    </form>
  );
}

export default function WorldRhubarbDay() {
  const { d } = useI18n();
  const w = d.wrdPage;

  useEffect(() => {
    if (window.location.hash === "#support") {
      setTimeout(() => document.getElementById("support")?.scrollIntoView({ behavior: "smooth" }), 200);
    }
  }, []);

  return (
    <main>
      <SEO page="wrd" />
      <section className="relative overflow-hidden pt-36 pb-24" data-testid="wrd-hero">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-6 sm:px-10 lg:grid-cols-[1.1fr_0.9fr] lg:px-16">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <Kicker>{w.kicker}</Kicker>
              <span className="border border-gold/60 px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-gold" data-testid="wrd-hero-status-badge">{w.badge}</span>
            </div>
            <h1 className="mt-8 font-display text-5xl font-bold uppercase leading-[0.95] tracking-tight sm:text-6xl lg:text-7xl" data-testid="wrd-headline">
              <MaskedLine delay={0.2}>{w.h1a}</MaskedLine>
              <MaskedLine delay={0.35}><span className="font-serif font-medium normal-case italic text-crimson dark:text-stalk">{w.h1b}</span></MaskedLine>
              <MaskedLine delay={0.5}>{w.h1c}</MaskedLine>
            </h1>
            <FadeIn delay={0.7}>
              <p className="mt-8 max-w-lg font-serif text-xl italic leading-relaxed text-foreground/85">{w.sub}</p>
              <p className="mt-4 max-w-lg text-sm leading-relaxed text-muted-foreground">{w.para}</p>
            </FadeIn>
            <FadeIn delay={0.85} className="mt-10 flex flex-wrap gap-4">
              <a href="#support" data-testid="wrd-hero-support-cta" className="group flex items-center gap-3 border border-crimson bg-crimson px-7 py-4 font-mono text-xs uppercase tracking-[0.25em] text-parchment transition-colors hover:bg-transparent hover:text-crimson">
                {w.cta} <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
              </a>
            </FadeIn>
          </div>
          <FadeIn delay={0.4}>
            <div className="specimen-frame overflow-hidden border border-foreground/15">
              <img src={HERO_IMG} alt="Fresh rhubarb stalks with carmine gradients" className="aspect-[4/5] w-full object-cover" />
            </div>
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{w.specimen}</p>
          </FadeIn>
        </div>
      </section>

      <SectionShell id="why" className="border-t border-border bg-card/40">
        <FadeIn><Kicker>{w.why.kicker}</Kicker></FadeIn>
        <div className="mt-14 grid gap-px border border-border bg-border md:grid-cols-2">
          {w.why.items.map((item, i) => {
            const Icon = WHY_ICONS[i];
            return (
              <FadeIn key={item.title} delay={i * 0.08} className="bg-background p-8" data-testid={`wrd-why-${i}`}>
                <Icon size={24} strokeWidth={1.5} className="text-crimson dark:text-stalk" />
                <h3 className="mt-5 font-display text-lg font-semibold">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.text}</p>
              </FadeIn>
            );
          })}
        </div>
      </SectionShell>

      <SectionShell id="proposal">
        <FadeIn><Kicker>{w.proposal.kicker}</Kicker></FadeIn>
        <div className="mt-10 grid gap-12 lg:grid-cols-[1fr_1.2fr]">
          <FadeIn delay={0.1}>
            <p className="text-base leading-relaxed text-muted-foreground">{w.proposal.para}</p>
            <div className="mt-8 border border-border bg-card/60 p-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{w.proposal.artifactLabel}</p>
              <p className="mt-2 font-serif text-lg italic">{w.proposal.artifact}</p>
            </div>
          </FadeIn>
          <div className="border border-border">
            {w.proposal.phases.map((ph, i) => (
              <FadeIn key={ph.p} delay={i * 0.08} className="border-b border-border p-6 last:border-0" data-testid={`wrd-proposal-phase-${i}`}>
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-crimson dark:text-stalk">{ph.p}</span>
                <h3 className="mt-2 font-display text-base font-semibold">{ph.t}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{ph.d}</p>
              </FadeIn>
            ))}
          </div>
        </div>
      </SectionShell>

      <SectionShell id="process" className="border-t border-border bg-moss text-parchment dark:bg-nightcard">
        <FadeIn><Kicker className="text-gold">{w.process.kicker}</Kicker></FadeIn>
        <FadeIn delay={0.1}>
          <h2 className="mt-6 max-w-2xl font-serif text-3xl leading-tight tracking-tight sm:text-4xl">{w.process.title}</h2>
        </FadeIn>
        <div className="mt-14 space-y-0 border border-parchment/15">
          {w.process.steps.map((st, i) => (
            <FadeIn key={st.t} delay={i * 0.06} className="grid gap-3 border-b border-parchment/15 p-6 last:border-0 sm:grid-cols-[70px_1fr_auto] sm:items-center" data-testid={`wrd-step-${i + 1}`}>
              <span className="font-serif text-4xl italic text-parchment/30">{String(i + 1).padStart(2, "0")}</span>
              <div>
                <h3 className="font-display text-base font-semibold">{st.t}</h3>
                <p className="mt-1 max-w-2xl text-sm leading-relaxed text-parchment/70">{st.d}</p>
              </div>
              <span className={`w-fit whitespace-nowrap border px-2.5 py-1 font-mono text-[9px] uppercase tracking-[0.15em] ${i === 0 ? "border-gold/60 text-gold" : "border-parchment/25 text-parchment/50"}`}>
                {st.status}
              </span>
            </FadeIn>
          ))}
        </div>
        <FadeIn delay={0.2}>
          <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.2em] text-parchment/50">{w.process.footer}</p>
        </FadeIn>
      </SectionShell>

      <SectionShell id="organizer">
        <div className="grid gap-12 lg:grid-cols-2 lg:items-start">
          <div>
            <FadeIn><Kicker>{w.organizer.kicker}</Kicker></FadeIn>
            <FadeIn delay={0.1}>
              <h2 className="mt-6 font-serif text-3xl leading-tight tracking-tight sm:text-4xl">
                {w.organizer.titleA}<span className="italic text-crimson dark:text-stalk">{w.organizer.titleAccent}</span>
              </h2>
            </FadeIn>
            <FadeIn delay={0.2}>
              <p className="mt-6 max-w-md text-base leading-relaxed text-muted-foreground">{w.organizer.para}</p>
            </FadeIn>
            <div className="mt-8 space-y-2">
              {w.organizer.examples.map((o, i) => (
                <FadeIn key={o} delay={i * 0.06} className="flex items-center justify-between border border-border bg-background px-5 py-4" data-testid={`wrd-organizer-example-${i}`}>
                  <span className="font-serif italic">{o}</span>
                  <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-gold">{w.organizer.open}</span>
                </FadeIn>
              ))}
            </div>
            <FadeIn delay={0.3}>
              <a href="/#join" data-testid="wrd-organizer-apply-cta" className="group mt-8 inline-flex items-center gap-3 border border-foreground/25 px-6 py-3.5 font-mono text-xs uppercase tracking-[0.25em] transition-colors hover:border-crimson hover:text-crimson">
                {w.organizer.cta} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
              </a>
            </FadeIn>
          </div>

          <div className="space-y-8">
            {[
              { panel: w.organizer.events, testid: "wrd-events-panel" },
              { panel: w.organizer.supporters, testid: "wrd-supporters-panel" },
              { panel: w.organizer.resources, testid: "wrd-resources-panel" },
            ].map(({ panel, testid }, i) => (
              <FadeIn key={testid} delay={i * 0.1} className="corner-ticks border border-border bg-card/60 p-8" data-testid={testid}>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground">{panel.label}</p>
                <p className="mt-4 font-serif text-2xl italic">{panel.title}</p>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{panel.text}</p>
              </FadeIn>
            ))}
          </div>
        </div>
      </SectionShell>

      <SectionShell id="support" className="border-t border-border bg-card/40">
        <div className="grid gap-14 lg:grid-cols-[1fr_1.1fr]">
          <FadeIn>
            <Kicker>{w.support.kicker}</Kicker>
            <h2 className="mt-6 font-serif text-3xl leading-tight tracking-tight sm:text-4xl">
              {w.support.titleA}<span className="italic text-crimson dark:text-stalk">{w.support.titleAccent}</span>
            </h2>
            <p className="mt-6 max-w-md text-base leading-relaxed text-muted-foreground">{w.support.para}</p>
          </FadeIn>
          <FadeIn delay={0.15}>
            <SupportForm />
          </FadeIn>
        </div>
      </SectionShell>
    </main>
  );
}
